// Spore-puffers (mushroom biome): sessile mushroom agents that puff a spore
// cloud when the player comes near. Telegraphed — idle → windup (a visible
// tell) → puff → cooldown — so the cloud is dodgeable (mastery, not a tax).

import type { Entity } from '../components'
import type { GameWorld } from '../world'
import { currentBiomeAt, BIOMES } from '../biomes'
import { morphologyFrom } from '../creature-recipe'
import { attachEyes } from './eyes'
import { spawnSporeCloud } from './hazards'
import { spawnSporeBlob } from './particles'
import { playSfx, panFor } from '../../engine/audio'
import { inWater } from '../dungeon/depth-field'
import { T } from '../tunables'

/** Keep a small puffer population in the mushroom floor around the player. */
export function maybeSpawnPuffer(world: GameWorld, player: Entity): void {
  if (currentBiomeAt(player.position.x, player.position.y) !== 'mushroom') return
  const count = [...world.with('puffer')].length
  if (count >= 2) return
  if (Math.random() >= 0.06) return

  const angle = Math.random() * Math.PI * 2
  const r = T.spawn.microbeSpawnRingMin + Math.random() * (T.spawn.microbeSpawnRingMax - T.spawn.microbeSpawnRingMin)
  const x = player.position.x + Math.cos(angle) * r
  const y = player.position.y + Math.sin(angle) * r
  if (!inWater(x, y, 0.3)) return
  const variant = currentBiomeAt(x, y)
  const puffer: Entity = {
    position: { x, y },
    velocity: { x: 0, y: 0 },
    wobble: { phase: Math.random() * Math.PI * 2, freq: 0.7, amp: 0.03 },
    renderable: { shape: 'microbe', radius: 0.16, tint: BIOMES[variant].predatorTint(), variant },
    // Rooted mushroom: gills + hyphae threads (a sessile fruiting body).
    morphology: morphologyFrom({ body: 'mushroom', ornaments: ['gills', 'hyphae'], count2: 8 }, Math.random()),
    puffer: { range: T.spore.pufferRange, state: 'idle', timer: 0 },
  }
  // Eyes → it's an agent (a watching mushroom), not food.
  attachEyes(puffer, [
    { offset: { x: -0.24, y: 0.30 }, radius: 0.24 },
    { offset: { x: 0.24, y: 0.30 }, radius: 0.24 },
  ], { angry: 0.4 })
  world.add(puffer)
}

export function sporePufferSystem(world: GameWorld, player: Entity, dt: number): void {
  const c = T.spore
  const despawn2 = T.spawn.microbeDespawnDistance * T.spawn.microbeDespawnDistance
  const toRemove: Entity[] = []
  for (const e of world.with('puffer', 'position')) {
    const p = e.puffer
    const dx = player.position.x - e.position.x
    const dy = player.position.y - e.position.y
    const d2 = dx * dx + dy * dy

    if (d2 > despawn2) { toRemove.push(e); continue }
    p.timer = Math.max(0, p.timer - dt)

    if (p.state === 'idle') {
      if (d2 < p.range * p.range && p.timer === 0) {
        p.state = 'windup'
        p.timer = c.pufferWindup
        playSfx('lunge_tick', { volume: 0.3, pan: panFor(e.position.x, player.position.x) })
      }
    } else if (p.state === 'windup') {
      // Telegraph: a few spore motes puff up before the release.
      if (Math.random() < 0.4) {
        spawnSporeBlob(world, e.position.x + (Math.random() - 0.5) * 0.2, e.position.y + 0.15, [0.86, 0.82, 0.95])
      }
      if (p.timer === 0) {
        spawnSporeCloud(world, e.position.x, e.position.y)
        playSfx('engulf_swell', { volume: 0.35, pan: panFor(e.position.x, player.position.x) })
        p.state = 'cooldown'
        p.timer = c.pufferCooldown
      }
    } else if (p.state === 'cooldown') {
      if (p.timer === 0) p.state = 'idle'
    }
  }
  for (const e of toRemove) {
    if (world.id(e) !== undefined) world.remove(e)
  }
}
